import type { Properties } from 'js-java-properties'
import { getProperty, listProperties, removeProperty } from 'js-java-properties'

import type { ConfigWithErrors } from '@/features/controller/configuration/models/raspiProperties/errors'

/**
 * What becomes of the value the model could not use. Said out loud, because the model is what the
 * next save writes: a value corrected in silence would leave the file changed under the user.
 */
function replacedBy(fallback: number | string | undefined) {
  return fallback === undefined
    ? 'the next save will remove it, leaving the controller default'
    : `the next save will replace it with ${fallback}`
}

function readUsableNumber(
  properties: Properties,
  key: string,
  label: string,
  fallback: number | undefined,
  isUsable: (value: number) => boolean,
): ConfigWithErrors<number | undefined> {
  const rawValue = getProperty(properties, key)
  if (rawValue === undefined) {
    return { config: fallback }
  }
  // `Number('')` is 0, so a key with nothing after the `=` would otherwise read as a configured zero
  if (rawValue.trim() === '') {
    return { config: fallback, errors: [`Empty ${label}; ${replacedBy(fallback)}`] }
  }
  const value = Number(rawValue)
  // Not just NaN: `Infinity` parses, and would be written back as the word
  if (!Number.isFinite(value) || !isUsable(value)) {
    return { config: fallback, errors: [`Invalid ${label} '${rawValue}'; ${replacedBy(fallback)}`] }
  }
  return { config: value }
}

/**
 * A number the file can be read for, or the fallback and a message saying what was wrong with it.
 * Treating an unusable value as an absent one would take the fallback with nothing said.
 *
 * @param label names the value, and has to identify it on the row the message lands on: a meter row
 *  can say `device number`, while the controller row needs `Modbus serial device number`
 * @param fallback stands in for a missing or unusable value, and is named in the message
 */
export function readNumberProperty(
  properties: Properties,
  key: string,
  label: string,
  fallback: number,
): ConfigWithErrors<number>
export function readNumberProperty(
  properties: Properties,
  key: string,
  label: string,
): ConfigWithErrors<number | undefined>
export function readNumberProperty(
  properties: Properties,
  key: string,
  label: string,
  fallback?: number,
): ConfigWithErrors<number | undefined> {
  return readUsableNumber(properties, key, label, fallback, () => true)
}

/**
 * As {@link readNumberProperty}, for the values the drawers refuse below one. Zero reaches the
 * controller as a broadcast unit id, a scale that zeroes every reading, or a port nothing listens
 * on, so the file is held to what the form would accept.
 */
export function readPositiveNumberProperty(
  properties: Properties,
  key: string,
  label: string,
  fallback: number,
): ConfigWithErrors<number>
export function readPositiveNumberProperty(
  properties: Properties,
  key: string,
  label: string,
): ConfigWithErrors<number | undefined>
export function readPositiveNumberProperty(
  properties: Properties,
  key: string,
  label: string,
  fallback?: number,
): ConfigWithErrors<number | undefined> {
  return readUsableNumber(properties, key, label, fallback, (value) => value > 0)
}

/**
 * One of an enum's values, or the fallback and a message. Looked up as an own key, so that
 * `constructor` and the rest of `Object.prototype` cannot answer for a value the enum never had.
 */
export function readEnumProperty<Type extends string>(
  properties: Properties,
  key: string,
  label: string,
  values: Record<string, Type>,
  fallback: Type,
): ConfigWithErrors<Type> {
  const rawValue = getProperty(properties, key)
  if (rawValue === undefined) {
    return { config: fallback }
  }
  if (Object.hasOwn(values, rawValue)) {
    return { config: values[rawValue] }
  }
  if (rawValue.trim() === '') {
    return { config: fallback, errors: [`Empty ${label}; ${replacedBy(fallback)}`] }
  }
  return { config: fallback, errors: [`Invalid ${label} '${rawValue}'; ${replacedBy(fallback)}`] }
}

/** For a value whose reader has a shape of its own to parse, and reports on it itself. */
export function getOptionalTransformedPropertyOrError<Type>(
  properties: Properties,
  key: string,
  transform: (value: string) => ConfigWithErrors<Type>,
): ConfigWithErrors<Type | undefined> {
  const value = getProperty(properties, key)
  return value !== undefined ? transform(value) : { config: undefined }
}

export function getBooleanProperty(properties: Properties, key: string): boolean {
  return getProperty(properties, key) === 'true'
}

export function removePropertiesWithPrefix(properties: Properties, prefix: string) {
  // Materialize "props to remove" first, to not modify the collection during iteration
  const propsToRemove = listProperties(properties)
    .filter(({ key }) => key.startsWith(prefix))
    .toArray()
  for (const { key } of propsToRemove) {
    removeProperty(properties, key)
  }
}
